import type { Case, PartyIndex, PartyInputs } from '@divorceos/schema';

/** How a field in a detail window is edited. */
export type FieldKind = 'money' | 'count' | 'percent' | 'bool' | 'text' | 'date' | 'readonly';

export interface FieldDef {
  label: string;
  kind: FieldKind;
  /** Section heading inserted before this field. */
  group?: string;
  get: (p: PartyInputs) => number | string | boolean;
  set: (p: PartyInputs, v: number | string | boolean) => void;
  /** Field is read-only when this returns true (e.g. auto-calculated hardship line 4). */
  lockedWhen?: (p: PartyInputs) => boolean;
  note?: string;
}

export type RowKind = 'children' | 'ncp' | 'filing' | 'leaf' | 'composite';

export interface RowDef {
  index: number;
  label: string;
  help: string;
  kind: RowKind;
  /** Detail window title (composite rows, and leaf rows that have a converter/detail). */
  dialogTitle?: string;
  /** Composite rows: the fields of the detail window. */
  fields?: FieldDef[];
  /** Leaf money rows: accessors. */
  get?: (p: PartyInputs) => number;
  set?: (p: PartyInputs, v: number) => void;
  /** Composite rows: the total shown in the grid (monthly). */
  total?: (p: PartyInputs) => number;
  /** Composite rows whose total is a count rather than money. */
  countTotal?: boolean;
}

const money = (label: string, get: (p: PartyInputs) => number, set: (p: PartyInputs, v: number) => void, extra: Partial<FieldDef> = {}): FieldDef => ({
  label, kind: 'money', get, set: (p, v) => set(p, typeof v === 'number' ? v : Number(v) || 0), ...extra,
});
const count = (label: string, get: (p: PartyInputs) => number, set: (p: PartyInputs, v: number) => void, extra: Partial<FieldDef> = {}): FieldDef => ({
  label, kind: 'count', get, set: (p, v) => set(p, Math.max(0, Math.round(typeof v === 'number' ? v : Number(v) || 0))), ...extra,
});

const incomeFields = (pick: (p: PartyInputs) => PartyInputs['otherTaxable']): FieldDef[] => [
  money('Short term capital gains', (p) => pick(p).capGainShort, (p, v) => { pick(p).capGainShort = v; }, { group: 'Investment income' }),
  money('Long term capital gains', (p) => pick(p).capGainLong, (p, v) => { pick(p).capGainLong = v; }),
  money('28% rate gain', (p) => pick(p).rateGain28, (p, v) => { pick(p).rateGain28 = v; }),
  money('Unrecaptured 1250 Gain', (p) => pick(p).unrecaptured1250, (p, v) => { pick(p).unrecaptured1250 = v; }),
  money('Qualified dividend income', (p) => pick(p).qualifiedDividends, (p, v) => { pick(p).qualifiedDividends = v; }),
  money('Non-qualified dividend income', (p) => pick(p).nonQualifiedDividends, (p, v) => { pick(p).nonQualifiedDividends = v; }),
  money('Royalty income', (p) => pick(p).royalty, (p, v) => { pick(p).royalty = v; }),
  money('Rental income', (p) => pick(p).rental, (p, v) => { pick(p).rental = v; }),
  money('Interest income', (p) => pick(p).interest, (p, v) => { pick(p).interest = v; }),
  money('Other investment income', (p) => pick(p).otherInvestment, (p, v) => { pick(p).otherInvestment = v; }),
  money('Social Security income', (p) => pick(p).socialSecurity, (p, v) => { pick(p).socialSecurity = v; }, { group: 'Other income' }),
  money('Unemployment income', (p) => pick(p).unemployment, (p, v) => { pick(p).unemployment = v; }),
  money('Traditional alimony received (Fed taxable)', (p) => pick(p).alimonyReceivedFedTaxable, (p, v) => { pick(p).alimonyReceivedFedTaxable = v; }),
  money('Traditional alimony received (Fed non taxable)', (p) => pick(p).alimonyReceivedFedNonTaxable, (p, v) => { pick(p).alimonyReceivedFedNonTaxable = v; }),
  money('Partner alimony received', (p) => pick(p).partnerAlimonyReceived, (p, v) => { pick(p).partnerAlimonyReceived = v; }),
  money('Other taxable income', (p) => pick(p).other, (p, v) => { pick(p).other = v; }),
  money('Subtract from state AGI', (p) => pick(p).subtractFromStateAgi, (p, v) => { pick(p).subtractFromStateAgi = v; }),
  money('Add to state AGI', (p) => pick(p).addToStateAgi, (p, v) => { pick(p).addToStateAgi = v; }),
];

/** Sum of the income-window fields that count toward the on-screen total (state-AGI adjustments excluded). */
export function incomeTotal(i: PartyInputs['otherTaxable']): number {
  return i.capGainShort + i.capGainLong + i.rateGain28 + i.unrecaptured1250 + i.qualifiedDividends + i.nonQualifiedDividends
    + i.royalty + i.rental + i.interest + i.otherInvestment + i.socialSecurity + i.unemployment
    + i.alimonyReceivedFedTaxable + i.alimonyReceivedFedNonTaxable + i.partnerAlimonyReceived + i.other;
}

export function exemptionTotal(p: PartyInputs): number {
  const e = p.exemptions;
  return e.personal + e.special + e.newSpouse + e.dependent;
}

export const ROWS: RowDef[] = [
  { index: 0, label: '#of children', help: 'Number of children of this relationship primarily with this parent; if SPLIT CUSTODY, use detail!', kind: 'children', dialogTitle: 'Children' },
  { index: 1, label: '% time with other parent', help: 'Percentage of the 365-day year the children spend with the non-custodial parent (the one they do not primarily live with)', kind: 'ncp', dialogTitle: 'Children' },
  { index: 2, label: 'Filing status', help: 'Federal tax filing status. Press spacebar to change. Use detail window for domestic partnerships.', kind: 'filing', dialogTitle: 'Tax Filing Statuses' },
  {
    index: 3, label: '# exemptions', help: 'Number of exemptions on return (including self) (not WHAs)', kind: 'composite', dialogTitle: 'Number of Exemptions', countTotal: true,
    total: exemptionTotal,
    fields: [
      { label: 'Automatic tally from children list (*)', kind: 'bool', get: (p) => p.exemptions.auto, set: (p, v) => { p.exemptions.auto = Boolean(v); } },
      count('Personal', (p) => p.exemptions.personal, (p, v) => { p.exemptions.personal = v; }),
      count('Special', (p) => p.exemptions.special, (p, v) => { p.exemptions.special = v; }),
      count('New spouse', (p) => p.exemptions.newSpouse, (p, v) => { p.exemptions.newSpouse = v; }),
      count('Dependent', (p) => p.exemptions.dependent, (p, v) => { p.exemptions.dependent = v; }),
      count('# of qualifying children for CCC', (p) => p.exemptions.qualifyingChildrenCcc, (p, v) => { p.exemptions.qualifyingChildrenCcc = v; }),
      count('# of qualifying children for EIC', (p) => p.exemptions.qualifyingChildrenEic, (p, v) => { p.exemptions.qualifyingChildrenEic = v; }),
      count('# of qualifying children for CTC', (p) => p.exemptions.qualifyingChildrenCtc, (p, v) => { p.exemptions.qualifyingChildrenCtc = v; }),
      count('# of CTC children aged < 6', (p) => p.exemptions.childrenUnder6, (p, v) => { p.exemptions.childrenUnder6 = v; }),
      count('# of other qualifying dependents', (p) => p.exemptions.otherDependents, (p, v) => { p.exemptions.otherDependents = v; }),
    ],
  },
  { index: 4, label: 'Wages+salary', help: 'Wage/salary/bonus/commission income/earning capacity', kind: 'leaf', dialogTitle: 'Wage Income', get: (p) => p.wages, set: (p, v) => { p.wages = v; } },
  { index: 5, label: 'Self-employed income', help: 'Self-employment income after business expenses', kind: 'leaf', get: (p) => p.selfEmployment, set: (p, v) => { p.selfEmployment = v; } },
  {
    index: 6, label: 'Other taxable income', help: 'All other federally taxable income (if loss, use minus)', kind: 'composite', dialogTitle: 'Other Taxable Income',
    total: (p) => incomeTotal(p.otherTaxable), fields: incomeFields((p) => p.otherTaxable),
  },
  {
    index: 7, label: 'TANF+CS received', help: 'Child support (not this relationship) and TANF received', kind: 'composite', dialogTitle: 'TANF and CS Received',
    total: (p) => p.tanfReceived + p.childSupportReceived,
    fields: [
      money('TANF payments received', (p) => p.tanfReceived, (p, v) => { p.tanfReceived = v; }),
      money('Child support received', (p) => p.childSupportReceived, (p, v) => { p.childSupportReceived = v; }),
    ],
  },
  {
    index: 8, label: 'Other nontaxble income', help: 'All other federally nontaxable income', kind: 'composite', dialogTitle: 'Non-taxable Income',
    total: (p) => p.nonTaxable.forChildSupportOnly + p.nonTaxable.taxExemptInterest + p.nonTaxable.other,
    fields: [
      money('Income used for Child Support only', (p) => p.nonTaxable.forChildSupportOnly, (p, v) => { p.nonTaxable.forChildSupportOnly = v; }),
      money('Tax exempt interest', (p) => p.nonTaxable.taxExemptInterest, (p, v) => { p.nonTaxable.taxExemptInterest = v; }),
      money('Other non-taxable income', (p) => p.nonTaxable.other, (p, v) => { p.nonTaxable.other = v; }),
      money('Total other nonrefundable credits', (p) => p.nonTaxable.otherNonrefundableCredits, (p, v) => { p.nonTaxable.otherNonrefundableCredits = v; }, { group: 'Form 1040, Schedule 3, other credits' }),
      money('Total other payments/refundable credits', (p) => p.nonTaxable.otherRefundableCredits, (p, v) => { p.nonTaxable.otherRefundableCredits = v; }),
    ],
  },
  {
    index: 9, label: 'New spouse income', help: 'Taxable income of new spouse (if any) (used for tax only)', kind: 'composite', dialogTitle: 'New Spouse Income',
    total: (p) => p.newSpouseIncome.wages + p.newSpouseIncome.selfEmployment + incomeTotal(p.newSpouseIncome),
    fields: [
      money('Wage income', (p) => p.newSpouseIncome.wages, (p, v) => { p.newSpouseIncome.wages = v; }),
      money('Self-employment income', (p) => p.newSpouseIncome.selfEmployment, (p, v) => { p.newSpouseIncome.selfEmployment = v; }),
      ...incomeFields((p) => p.newSpouseIncome),
    ],
  },
  { index: 10, label: '401(k) employee contrib', help: '401(k) employee contribution', kind: 'leaf', get: (p) => p.employee401k, set: (p, v) => { p.employee401k = v; } },
  {
    index: 11, label: 'Adjustments to income', help: 'Form 1040 Adjustments to Income.', kind: 'composite', dialogTitle: 'Adjustments to Income (ATI)',
    total: (p) => p.adjustments.sepSimple + p.adjustments.otherAti + p.adjustments.charitable,
    fields: [
      money('SEP, SIMPLE plans', (p) => p.adjustments.sepSimple, (p, v) => { p.adjustments.sepSimple = v; }),
      money('Other ATIs', (p) => p.adjustments.otherAti, (p, v) => { p.adjustments.otherAti = v; }),
      money('Charitable contributions', (p) => p.adjustments.charitable, (p, v) => { p.adjustments.charitable = v; }),
    ],
  },
  {
    index: 12, label: 'SS paid prev marriage', help: 'Deductible (spousal) support paid re other marriages', kind: 'composite', dialogTitle: 'Spousal Support Paid',
    total: (p) => Object.values(p.ssPaidPrior).reduce((a, b) => a + b, 0),
    fields: [
      money('Traditional SS paid by THIS spouse (Fed tax ded)', (p) => p.ssPaidPrior.thisSpouseFedDeductible, (p, v) => { p.ssPaidPrior.thisSpouseFedDeductible = v; }),
      money('Traditional SS paid by THIS spouse (Fed non tax ded)', (p) => p.ssPaidPrior.thisSpouseFedNonDeductible, (p, v) => { p.ssPaidPrior.thisSpouseFedNonDeductible = v; }),
      money('Traditional SS paid by new spouse (Fed tax ded)', (p) => p.ssPaidPrior.newSpouseFedDeductible, (p, v) => { p.ssPaidPrior.newSpouseFedDeductible = v; }),
      money('Traditional SS paid by new spouse (Fed non tax ded)', (p) => p.ssPaidPrior.newSpouseFedNonDeductible, (p, v) => { p.ssPaidPrior.newSpouseFedNonDeductible = v; }),
      money('Partner SS paid by THIS spouse', (p) => p.ssPaidPrior.partnerByThisSpouse, (p, v) => { p.ssPaidPrior.partnerByThisSpouse = v; }),
      money('Partner SS paid by new spouse', (p) => p.ssPaidPrior.partnerByNewSpouse, (p, v) => { p.ssPaidPrior.partnerByNewSpouse = v; }),
    ],
  },
  { index: 13, label: 'CS paid prev marriage', help: 'Nondeductible (child) support paid re other relationships', kind: 'leaf', get: (p) => p.csPaidPrior, set: (p, v) => { p.csPaidPrior = v; } },
  {
    index: 14, label: 'Health insurance', help: 'Health insurance expenses (self, spouse, dependents)', kind: 'composite', dialogTitle: 'Health Insurance',
    total: (p) => p.healthInsurance.paidByParty + p.healthInsurance.wageDeductionPreTax,
    fields: [
      money('Paid by party (less premium tax credit)', (p) => p.healthInsurance.paidByParty, (p, v) => { p.healthInsurance.paidByParty = v; }),
      money('Wage deduction (pre-tax)', (p) => p.healthInsurance.wageDeductionPreTax, (p, v) => { p.healthInsurance.wageDeductionPreTax = v; }),
      money('CA individual responsibility penalty', (p) => p.healthInsurance.caIsrPenalty, (p, v) => { p.healthInsurance.caIsrPenalty = v; }, { note: 'Switch to annual figures to enter CA individual responsibility penalty' }),
    ],
  },
  { index: 15, label: 'Other medical expense', help: 'Total recurring other medical, dental and drug expenses (do not include health insurance)', kind: 'leaf', get: (p) => p.otherMedical, set: (p, v) => { p.otherMedical = v; } },
  { index: 16, label: 'Property tax expense', help: 'Total recurring property tax expenses', kind: 'leaf', get: (p) => p.propertyTax, set: (p, v) => { p.propertyTax = v; } },
  {
    index: 17, label: 'Ded interest expense', help: 'Total recurring interest expenses', kind: 'composite', dialogTitle: 'Interest Expenses',
    total: (p) => Object.values(p.interestExpense).reduce((a, b) => a + b, 0),
    fields: [
      money('Investment interest expense', (p) => p.interestExpense.investmentInterest, (p, v) => { p.interestExpense.investmentInterest = v; }),
      money('Form 4952 line 4e', (p) => p.interestExpense.form4952Line4e, (p, v) => { p.interestExpense.form4952Line4e = v; }),
      money('Form 4952 line 4g', (p) => p.interestExpense.form4952Line4g, (p, v) => { p.interestExpense.form4952Line4g = v; }),
      money('Home mortgage interest and points', (p) => p.interestExpense.homeMortgage, (p, v) => { p.interestExpense.homeMortgage = v; }),
    ],
  },
  { index: 18, label: 'Contribution deduction', help: 'Total recurring charitable contribution deductions', kind: 'leaf', get: (p) => p.contributions, set: (p, v) => { p.contributions = v; } },
  {
    index: 19, label: 'Misc tax deductions', help: 'Total recurring federal miscellaneous deduction expenses', kind: 'composite', dialogTitle: 'Miscellaneous Tax Deductions',
    total: (p) => Object.values(p.miscDeductions).reduce((a, b) => a + b, 0),
    fields: [
      money('State sales taxes', (p) => p.miscDeductions.stateSalesTax, (p, v) => { p.miscDeductions.stateSalesTax = v; }),
      money('Investment expenses', (p) => p.miscDeductions.investmentExpenses, (p, v) => { p.miscDeductions.investmentExpenses = v; }),
      money('Other expenses', (p) => p.miscDeductions.other, (p, v) => { p.miscDeductions.other = v; }),
    ],
  },
  { index: 20, label: 'Qual bus income ded', help: 'Qualified Business Income Deduction', kind: 'leaf', get: (p) => p.qbid, set: (p, v) => { p.qbid = v; } },
  { index: 21, label: 'Required union dues', help: 'Union dues required as condition of employment', kind: 'leaf', get: (p) => p.unionDues, set: (p, v) => { p.unionDues = v; } },
  {
    index: 22, label: 'Mandatory retirement', help: 'Mandatory retirement and pension fund contributions', kind: 'composite', dialogTitle: 'Mandatory Retirement',
    total: (p) => p.mandatoryRetirement.qualifiedAti + p.mandatoryRetirement.other,
    fields: [
      money('Qualified adjustments to income', (p) => p.mandatoryRetirement.qualifiedAti, (p, v) => { p.mandatoryRetirement.qualifiedAti = v; }),
      money('Other contributions', (p) => p.mandatoryRetirement.other, (p, v) => { p.mandatoryRetirement.other = v; }),
    ],
  },
  {
    index: 23, label: 'Hardship deduction', help: 'Hardship deduction for medical expenses and losses', kind: 'composite', dialogTitle: 'Hardship Deduction',
    total: (p) => p.hardship.totalFc4071b + p.hardship.extraordinaryHealth + p.hardship.catastrophicLosses,
    fields: [
      count('# of other supported children', (p) => p.hardship.otherSupportedChildren, (p, v) => { p.hardship.otherSupportedChildren = v; }),
      { label: 'Multiply FC4071(b) hardship by', kind: 'percent', get: (p) => p.hardship.multiplyPercent, set: (p, v) => { p.hardship.multiplyPercent = Number(v) || 0; } },
      { label: 'Calculate FC4071(b) hardship?', kind: 'bool', get: (p) => p.hardship.calculate, set: (p, v) => { p.hardship.calculate = Boolean(v); } },
      money('Total FC4071(b) hardship (line 4, auto)', (p) => p.hardship.totalFc4071b, (p, v) => { p.hardship.totalFc4071b = v; }, { lockedWhen: (p) => p.hardship.calculate }),
      money('Extraordinary health expenses', (p) => p.hardship.extraordinaryHealth, (p, v) => { p.hardship.extraordinaryHealth = v; }),
      money('Uninsured catastrophic losses', (p) => p.hardship.catastrophicLosses, (p, v) => { p.hardship.catastrophicLosses = v; }),
    ],
  },
  {
    index: 24, label: 'Other GDL deductions', help: 'Other deductions per local discretionary guideline', kind: 'composite', dialogTitle: 'Other Guideline Deductions',
    total: (p) => p.otherGuidelineDeductions.jobRelated + p.otherGuidelineDeductions.other,
    fields: [
      money('Necessary job-related expenses', (p) => p.otherGuidelineDeductions.jobRelated, (p, v) => { p.otherGuidelineDeductions.jobRelated = v; }),
      money('Other guideline deductions', (p) => p.otherGuidelineDeductions.other, (p, v) => { p.otherGuidelineDeductions.other = v; }),
    ],
  },
  {
    index: 25, label: 'Child care expenses', help: 'Mandated add-ons (such as child care expenses)', kind: 'composite', dialogTitle: 'Child Care Expenses',
    total: (p) => Object.values(p.childCare).reduce((a, b) => a + b, 0),
    fields: [
      money('Care for child(ren) of this relationship', (p) => p.childCare.thisRelationship, (p, v) => { p.childCare.thisRelationship = v; }),
      money('Expenses for visits school and travel', (p) => p.childCare.visitsSchoolTravel, (p, v) => { p.childCare.visitsSchoolTravel = v; }),
      money('Uninsured health expenses', (p) => p.childCare.uninsuredHealth, (p, v) => { p.childCare.uninsuredHealth = v; }),
      money('Care for other child(ren) in the home', (p) => p.childCare.otherChildrenInHome, (p, v) => { p.childCare.otherChildrenInHome = v; }),
    ],
  },
];

export const ROW_COUNT = ROWS.length;

/** Row 0 value: children of this relationship primarily with this parent. */
export function childrenWith(c: Case, party: PartyIndex): number {
  if (c.children.length === 0) return c.parties[party].childrenAsCustodial;
  return c.children.filter((ch) => ch.custodialParent === party).length;
}
